import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import {
    loadClaimEventStatuses,
    loadClaimEventTypes,
    loadClaimQueueActionStatusDetails
} from '../../../store/directusService';
import {
    clearClaimQueues,
    clearParticipantsSearch,
    searchClaimEvents,
    searchClaimQueue,
    searchParticipants
} from '../../../store/participantService';
import { stableSort } from '../../../utils/sortFunctions';
import { addFullNameToArray } from '../../../utils/userArrayUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import ActionEditor from '../../claims/validation/ActionEditor';
import SingleSelect from '../../formElements/SingleSelect';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import StatusUpdateModal from './statusUpdateModal/StatusUpdateModal';
import ClaimTableRow from './ClaimTableRow';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from '../homeStyles/claims.module.css';

const headCells = [
    { id: 'ptCode', numeric: false, label: 'Participant ID' },
    { id: 'firstName', numeric: false, label: 'First Name' },
    { id: 'lastName', numeric: false, label: 'Last Name' },
    { id: 'eventType', numeric: false, label: 'Event Type' },
    { id: 'statusName', numeric: false, label: 'Status' },
    { id: 'actionStatus', numeric: false, label: 'Action Status', align: 'center' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'eventType',
    page: 0,
    rowsPerPage: 100
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const acceptedUpdateStatusRoles = [ADVISER, MANAGER, PRAP, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const adviserRoles = [ADVISER, MANAGER];

const ClaimManagement = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [adviserId, setAdviserId] = useState('');
    const [advisers, setAdvisers] = useState([]);
    const [rows, setRows] = useState([]);
    const [claimsForRows, setClaimsForRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});

    const [ptCodesToSearch, setPtCodesToSearch] = useState([]);
    const [ptCodesByAdviser, setPtCodesByAdviser] = useState({});
    const [ptsByAdviser, setPtsByAdviser] = useState({});

    const [toStatusesIds, setToStatusesIds] = useState([]);
    const [carActionStatusId, setCarActionStatusId] = useState('');
    const [failStatusId, setFailStatusId] = useState('');

    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [openRowId, setOpenRowId] = useState('');
    const [isOpenStatusModal, setIsOpenStatusModal] = useState(false);
    const [isOpenActionModal, setIsOpenActionModal] = useState(false);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );
    const claimQueueActionStatusDetails = useSelector(
        (state) => state.entities.directusService.claimQueueActionStatusDetails
    );
    const claimEventTypes = useSelector((state) => state.entities.directusService.claimEventTypes);
    const claimEvents = useSelector((state) => state.entities.participantService.claimEvents);
    const claimQueues = useSelector((state) => state.entities.participantService.claimQueues);
    const claimQueuesMetaData = useSelector(
        (state) => state.entities.participantService.claimQueuesMetaData
    );
    const loadingClaimQueuesSearch = useSelector(
        (state) => state.entities.participantService.loadingClaimQueuesSearch
    );
    const participants = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const loadingParticipantsSearch = useSelector(
        (state) => state.entities.participantService.loadingParticipantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );

    // USE EFFECTS
    useEffect(() => {
        claimEventTypes?.length < 1 && dispatch(loadClaimEventTypes());
        claimEventStatuses?.length < 1 && dispatch(loadClaimEventStatuses());
        claimQueueActionStatusDetails?.length < 1 && dispatch(loadClaimQueueActionStatusDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (loggedInUser.roles.some((el) => adviserRoles.includes(el))) {
            setAdviserId(loggedInUser.id);
            onAddAdvisers([loggedInUser]);
        }
    }, [loggedInUser.id]);

    useEffect(() => {
        if (!claimEventStatuses.length) return;
        setFailStatusId(
            claimEventStatuses.find((el) => el.name.toLowerCase() === 'fail')?.id || ''
        );
    }, [claimEventStatuses]);

    useEffect(() => {
        if (!claimQueueActionStatusDetails.length) return;
        setCarActionStatusId(
            claimQueueActionStatusDetails.find(
                (el) => el.name.toLowerCase() === 'corrective action required'
            )?.id || ''
        );
    }, [claimQueueActionStatusDetails]);

    useEffect(() => {
        if (!users.length) return;
        let updatedAdvisers = users.filter((el) =>
            el.userTypes.some((entry) => adviserRoles.includes(entry.role))
        );
        if (adviserId && !updatedAdvisers.some((el) => el.id === adviserId)) {
            // Put selected adviser at the top of dropdown if it's not in the updated advisers array
            const selectedAdviser = advisers.find((el) => el.id === adviserId);
            updatedAdvisers = [selectedAdviser, ...updatedAdvisers];
        }
        onAddAdvisers(updatedAdvisers);
    }, [users]);

    useEffect(() => {
        setClaimsForRows([]);
        setPtCodesToSearch([]);
        dispatch(clearParticipantsSearch());
        dispatch(clearClaimQueues());
        if (adviserId && failStatusId && carActionStatusId) {
            dispatch(
                searchClaimQueue(
                    {
                        participantUserId: adviserId,
                        or: {
                            statusIds: [failStatusId],
                            actionStatusId: carActionStatusId
                        }
                    },
                    0
                )
            );
        }
    }, [adviserId, failStatusId, carActionStatusId]);

    useEffect(() => {
        if (loadingClaimQueuesSearch) return;
        if (
            !claimQueuesMetaData?.last &&
            claimQueues?.length > 0 &&
            !loadingClaimQueuesSearch &&
            'number' in claimQueuesMetaData
        ) {
            dispatch(
                searchClaimQueue(
                    {
                        participantUserId: adviserId,
                        or: {
                            statusIds: [failStatusId],
                            actionStatusId: carActionStatusId
                        }
                    },
                    claimQueuesMetaData.number + 1
                )
            );
        } else if (claimQueuesMetaData?.last && claimQueues.length > 0) {
            const newPtCodes = [...new Set(claimQueues.map((el) => el.ptCode))];
            if (!(adviserId in ptCodesByAdviser) || !(adviserId in ptsByAdviser)) {
                dispatch(searchParticipants({ ptCodes: newPtCodes }, 0));
                setPtCodesToSearch(newPtCodes);
                setPtCodesByAdviser((prev) => ({ ...prev, [adviserId]: newPtCodes }));
            } else {
                if (adviserId in ptsByAdviser) configEligibilityGroups(ptsByAdviser[adviserId]);
            }
            setClaimsForRows(
                claimQueues.filter(
                    (el) => el.statusId === failStatusId || el.actionStatusId === carActionStatusId
                )
            );
        } else if (claimQueuesMetaData?.last && claimQueues.length < 1) {
            setRows([]);
            setClaimsForRows([]);
            setLoadingContentMessage('No claims found');
        }
    }, [claimQueues, claimQueuesMetaData, loadingClaimQueuesSearch]);

    useEffect(() => {
        if (
            ptCodesToSearch.length > 0 &&
            participants?.length > 0 &&
            !loadingParticipantsSearch &&
            'number' in participantsSearchMetaData &&
            !participantsSearchMetaData.last
        ) {
            dispatch(
                searchParticipants(
                    { ptCodes: ptCodesToSearch },
                    participantsSearchMetaData.number + 1
                )
            );
        } else if (participantsSearchMetaData?.last) {
            if (!participants.length) {
                setRows([]);
                setClaimsForRows([]);
                setLoadingContentMessage('No claims found');
                dispatch(clearClaimQueues());
            } else {
                setPtsByAdviser((prev) => ({ ...prev, [adviserId]: participants }));
                configEligibilityGroups(participants);
            }
        }
    }, [participants, participantsSearchMetaData, loadingParticipantsSearch]);

    useEffect(() => {
        if (!(adviserId in ptsByAdviser) || !claimQueuesMetaData?.last) return;
        if (
            ptsByAdviser[adviserId]?.length < 1 ||
            claimsForRows.length < 1 ||
            claimEvents?.length < 1
        ) {
            setRows([]);
            setLoadingContentMessage('No claims found');
            return;
        }
        const updatedClaims = claimsForRows
            .map((el) => {
                const pt = ptsByAdviser[adviserId].find((entry) => entry.ptCode === el.ptCode);
                return {
                    ...el,
                    eventName: el.eventType || 'zz',
                    statusName: el.status || 'zz',
                    firstName: pt?.firstName || 'zz',
                    lastName: pt?.lastName || 'zz',
                    actionStatus: el?.actionStatus || 'zz',
                    eligibilityGroupId: pt?.eligibilityGroupId || ''
                };
            })
            .map((el) => {
                if (claimEvents.length < 1 || claimEventStatuses.length < 1) return { ...el };
                let onUpdateStatus;
                const toStatuses =
                    claimEvents
                        .find(
                            (entry) =>
                                entry.eligibilityGroupId === el.eligibilityGroupId &&
                                entry.claimTypeId === el.eventTypeId
                        )
                        ?.claimTypeStatuses?.find((entry) => entry.statusId === failStatusId)
                        ?.claimTypeToStatusIds || [];
                const failToStatuses = claimEventStatuses
                    .filter(
                        (entry) =>
                            entry.name.toLowerCase() === 'resubmitted' ||
                            entry.name.toLowerCase() === 'escalated'
                    )
                    .map((entry) => entry.id)
                    .filter((entry) => toStatuses.includes(entry));
                if (
                    hasRole(acceptedUpdateStatusRoles, loggedInUser.roles) &&
                    el.statusId === failStatusId &&
                    failToStatuses.length > 0
                )
                    onUpdateStatus = () => {
                        setIsOpenStatusModal(true);
                        setSelectedRow(el);
                        setToStatusesIds(
                            claimEventStatuses.filter((entry) => failToStatuses.includes(entry.id))
                        );
                    };

                return { ...el, onUpdateStatus };
            });
        setRows(updatedClaims);
    }, [claimEvents, claimQueues, claimQueuesMetaData]);

    // HELPER FNS
    const onAddAdvisers = (advisers) => {
        const updatedAdvisers = addFullNameToArray(advisers);
        setAdvisers(updatedAdvisers);
    };

    const configEligibilityGroups = (participants) => {
        const eligibilityGroupIds = [...new Set(participants.map((el) => el.eligibilityGroupId))];
        eligibilityGroupIds.length && dispatch(searchClaimEvents({ eligibilityGroupIds }));
    };

    const cleanUp = () => {
        dispatch(clearParticipantsSearch());
        dispatch(clearClaimQueues());
    };

    // EVENT HANDLERS
    const onEditAction = (row) => {
        setSelectedRow(row);
        setIsOpenActionModal(true);
    };

    const onActionModalCancel = () => {
        setIsOpenActionModal(false);
        setSelectedRow({});
    };

    const onUpdateActionStatus = () => {
        setIsOpenActionModal(false);
        setSelectedRow({});
    };

    const onToggleDropdown = (id) => setOpenRowId(id);

    // RENDER
    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <ClaimTableRow
                key={el.id}
                row={el}
                roles={loggedInUser.roles}
                acceptedRoles={acceptedRoles}
                toggleDropdown={onToggleDropdown}
                openRowId={openRowId}
                onEditAction={onEditAction}
            />
        ));
    };

    return (
        <div className={app.pageContainer}>
            <div className={local.topBar}>
                <div className={local.iconContainer}>
                    <AccountBalanceIcon />
                </div>

                <div className={form.minWidthField}>
                    <SingleSelect
                        id={'adviserId'}
                        label={'Adviser'}
                        placeholder="Select adviser..."
                        menuItems={advisers || []}
                        selectedId={adviserId || ''}
                        selected={advisers?.find((el) => el.id === adviserId) || {}}
                        onChange={(chosenId) => setAdviserId(chosenId)}
                    />
                </div>
            </div>

            <div>
                {rows.length < 1 ? (
                    !adviserId ? (
                        'Select an adviser'
                    ) : (
                        <LoadingSpinner content={loadingContentMessage} />
                    )
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            {isOpenActionModal && (
                <ActionEditor
                    roles={loggedInUser.roles}
                    formType="edit"
                    isOpen={isOpenActionModal}
                    selectedRow={selectedRow}
                    onCancel={onActionModalCancel}
                    onUpdateAction={onUpdateActionStatus}
                />
            )}
            <StatusUpdateModal
                isOpen={isOpenStatusModal}
                setOpen={setIsOpenStatusModal}
                claimStatus={selectedRow}
                toStatusesIds={toStatusesIds}
            />
        </div>
    );
};

export default ClaimManagement;
